import { nanoid } from 'nanoid';
import type { Bounds, CanvasElement, Point } from './types.js';

export const newId = (): string => nanoid(12);

export const now = (): number => Date.now();

// Deterministic seed for roughjs per element (0..2^31)
export const newSeed = (): number => Math.floor(Math.random() * 2 ** 31);

// Nonce for Yjs conflict tiebreak
export const newNonce = (): number => Math.floor(Math.random() * 2 ** 31);

// Bounds ------------------------------------------------------------

export const boundsOf = (el: Pick<CanvasElement, 'x' | 'y' | 'width' | 'height'>): Bounds => ({
  x1: el.x,
  y1: el.y,
  x2: el.x + el.width,
  y2: el.y + el.height,
});

export const pointInBounds = (p: Point, b: Bounds): boolean =>
  p.x >= b.x1 && p.x <= b.x2 && p.y >= b.y1 && p.y <= b.y2;

export const boundsIntersect = (a: Bounds, b: Bounds): boolean =>
  a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;

export const boundsContains = (outer: Bounds, inner: Bounds): boolean =>
  outer.x1 <= inner.x1 && outer.y1 <= inner.y1 && outer.x2 >= inner.x2 && outer.y2 >= inner.y2;

export const mergeBounds = (bs: Bounds[]): Bounds | null => {
  if (bs.length === 0) return null;
  const first = bs[0]!;
  let x1 = first.x1;
  let y1 = first.y1;
  let x2 = first.x2;
  let y2 = first.y2;
  for (let i = 1; i < bs.length; i++) {
    const b = bs[i]!;
    if (b.x1 < x1) x1 = b.x1;
    if (b.y1 < y1) y1 = b.y1;
    if (b.x2 > x2) x2 = b.x2;
    if (b.y2 > y2) y2 = b.y2;
  }
  return { x1, y1, x2, y2 };
};

// Fractional index (base62) for z-order ----------------------------
// Rough implementation. Full spec: https://observablehq.com/@dgreensp/implementing-fractional-indexing

const CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const MID = 'V'; // roughly middle

export const midIndex = (): string => MID;

export const indexBetween = (a: string | null, b: string | null): string => {
  const left = a ?? '';
  const right = b ?? '';
  // Simple midpoint: append 'V' until unique. For MVP this is enough.
  let i = 0;
  let prefix = '';
  while (true) {
    const lc = left[i] ?? '0';
    const rc = right[i] ?? 'z';
    const li = CHARS.indexOf(lc);
    const ri = CHARS.indexOf(rc);
    if (ri - li > 1) {
      return prefix + CHARS[Math.floor((li + ri) / 2)];
    }
    prefix += lc;
    i++;
    if (i > 30) throw new Error('index depth exhausted');
  }
};
